import { Reducer } from "redux";
import { OrganizationKeyName } from "../../model/Organization";
import { UserStatus } from "../../model/UserStatus";
import { UserAutocompleteItem } from "../../service/api-response/UsersAutocompleteSuccessResponse";
import * as userActions from "./userActions";
import { User } from "../../model/User";
import { GetUserListSubtype } from "./userActions";
import * as authActions from "./../auth/authActions";
import * as immutable from "immutable";
import { UserBalance } from "../../service/api-response/UserBalanceSuccessResponse";

interface OrganizationRecordFilters extends immutable.Map<string, any> {}
interface OrganizationRecordUserList extends immutable.List<User> {}

interface OrganizationRecordProps {
  users: OrganizationRecordUserList;
  usersTotal: number;
  nextPageUrl?: string;
  hasMore: boolean;
  filters: OrganizationRecordFilters;
  isLoadingUsers: boolean;
  isApprovingUser: { [index: number]: boolean };
  isDenyingUser: { [index: number]: boolean };
  isLoadingBalance: { [index: number]: boolean };
  isWithdrawingBalance: { [index: number]: boolean };
  balances: { [index: number]: UserBalance[] | false };
  isLoadingAutocomplete: boolean;
  autocompleteUsers: UserAutocompleteItem[];
}

interface OrganizationRecord
  extends immutable.Record<OrganizationRecordProps> {}

const makeOrganizationRecord = immutable.Record<OrganizationRecordProps>({
  users: immutable.List(),
  usersTotal: 0,
  nextPageUrl: undefined,
  hasMore: false,
  filters: immutable.Map(),
  isLoadingUsers: false,
  isApprovingUser: {},
  isDenyingUser: {},
  isLoadingAutocomplete: false,
  autocompleteUsers: [],
  isLoadingBalance: {},
  isWithdrawingBalance: {},
  balances: {},
});

export interface UserState {
  orgMap: immutable.Map<OrganizationKeyName, OrganizationRecord>;
}

const initialState: UserState = {
  orgMap: immutable.Map(),
};

type SupportedActions =
  | userActions.GetUserListAction
  | userActions.GetUserListSuccessAction
  | userActions.GetUserListFailAction
  | userActions.SetFiltersUserListAction
  | userActions.ResetFiltersUserListAction
  | userActions.ApproveUserAction
  | userActions.ApproveUserFailAction
  | userActions.ApproveUserSuccessAction
  | userActions.DenyUserAction
  | userActions.DenyUserFailAction
  | userActions.DenyUserSuccessAction
  | authActions.SelectOrganizationSuccessAction
  | userActions.AutocompleteUsersPopupAction
  | userActions.AutocompleteUsersPopupFailAction
  | userActions.AutocompleteUsersPopupSuccessAction;

const getOrgMapWithOrganizationRecord = (
  state: UserState,
  organization: OrganizationKeyName
) => {
  let orgMap = state.orgMap;

  if (!state.orgMap.has(organization)) {
    orgMap = orgMap.set(organization, new makeOrganizationRecord());
  }

  return orgMap;
};

export const userReducer: Reducer<UserState, SupportedActions> = (
  state = initialState,
  action
) => {
  switch (action.type) {
    case userActions.GET_USER_LIST: {
      if (action.subtype === GetUserListSubtype.LoadFirstPage) {
        const orgMap = getOrgMapWithOrganizationRecord(
          state,
          action.organization
        );

        return {
          ...state,
          orgMap: orgMap
            .updateIn(
              [action.organization, "users"],
              (list: OrganizationRecordUserList) => list.clear()
            )
            .setIn([action.organization, "isLoadingUsers"], true)
            .setIn([action.organization, "usersTotal"], 0),
        };
      } else {
        const orgMap = getOrgMapWithOrganizationRecord(
          state,
          action.organization
        );

        return {
          ...state,
          orgMap: orgMap.setIn([action.organization, "isLoadingUsers"], true),
        };
      }
    }
    case userActions.GET_USER_LIST_SUCCESS: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap
          .updateIn(
            [action.organization, "users"],
            (list: OrganizationRecordUserList) => list.push(...action.users)
          )
          .setIn([action.organization, "hasMore"], action.hasMore)
          .setIn([action.organization, "nextPageUrl"], action.nextPageUrl)
          .setIn([action.organization, "isLoadingUsers"], false)
          .setIn([action.organization, "usersTotal"], action.totalCount),
      };
    }
    case userActions.GET_USER_LIST_FAIL: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.setIn([action.organization, "isLoadingUsers"], false),
      };
    }

    case userActions.SET_FILTERS_USER_LIST: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn([action.organization, "filters"], () =>
          immutable.fromJS(action.values)
        ),
      };
    }
    case userActions.RESET_FILTERS_USER_LIST: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn(
          [action.organization, "filters"],
          (filters: OrganizationRecordFilters) => filters.clear()
        ),
      };
    }
    case userActions.APPROVE_USER: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn(
          [action.organization, "isApprovingUser"],
          (map) => ({
            ...map,
            [action.userId]: true,
          })
        ),
      };
    }
    case userActions.APPROVE_USER_SUCCESS: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap
          .updateIn(
            [action.organization, "users"],
            (users: OrganizationRecordUserList) =>
              users.map((user) => {
                if (parseInt(user.id, 10) === action.userId) {
                  return {
                    ...user,
                    status: UserStatus.Accepted,
                  };
                }

                return user;
              })
          )
          .updateIn([action.organization, "isApprovingUser"], (map) => ({
            ...map,
            [action.userId]: false,
          })),
      };
    }
    case userActions.APPROVE_USER_FAIL: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn(
          [action.organization, "isApprovingUser"],
          (map) => {
            return {
              ...map,
              [action.userId]: false,
            };
          }
        ),
      };
    }
    case userActions.DENY_USER: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn(
          [action.organization, "isDenyingUser"],
          (map) => {
            return {
              ...map,
              [action.userId]: true,
            };
          }
        ),
      };
    }
    case userActions.DENY_USER_SUCCESS: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap
          .updateIn(
            [action.organization, "users"],
            (users: OrganizationRecordUserList) =>
              users.map((user) => {
                if (parseInt(user.id, 10) === action.userId) {
                  return {
                    ...user,
                    status: UserStatus.Denied,
                  };
                }

                return user;
              })
          )
          .updateIn([action.organization, "isDenyingUser"], (map) => {
            return {
              ...map,
              [action.userId]: false,
            };
          }),
      };
    }
    case userActions.DENY_USER_FAIL: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.updateIn(
          [action.organization, "isDenyingUser"],
          (map) => {
            return {
              ...map,
              [action.userId]: false,
            };
          }
        ),
      };
    }
    case userActions.AUTOCOMPLETE_USERS_POPUP: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.setIn(
          [action.organization, "isLoadingAutocomplete"],
          true
        ),
      };
    }
    case userActions.AUTOCOMPLETE_USERS_POPUP_SUCCESS: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap
          .setIn([action.organization, "isLoadingAutocomplete"], false)
          .setIn([action.organization, "autocompleteUsers"], action.users),
      };
    }
    case userActions.AUTOCOMPLETE_USERS_POPUP_FAIL: {
      const orgMap = getOrgMapWithOrganizationRecord(
        state,
        action.organization
      );

      return {
        ...state,
        orgMap: orgMap.setIn(
          [action.organization, "isLoadingAutocomplete"],
          false
        ),
      };
    }

    default: {
      return state;
    }
  }
};
